<template>
  <v-container fluid fill-height>
    <v-layout>
      <v-flex>
        <v-row class="fill-height" no-gutters>
          <v-col class="col-12">
            <v-row>
              <v-col class="col-12">
                <v-card-title class="px-0">
                  <v-row class="manual-header-row align-center" no-gutters>
                    <v-col class="col-auto">
                      <h1 class="primary-color manual-header-title">NEXT Semantica</h1>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto" class="manual-upload-col d-flex align-center">
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            icon
                            color="primary"
                            class="mr-2"
                            v-bind="attrs"
                            v-on="on"
                            @click="$router.push('/coherence-evaluation')"
                          >
                            <v-icon>mdi-clipboard-check-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>QA Review</span>
                      </v-tooltip>
                      <v-btn class="new-item-btn" @click="openUploadVideo">
                        <v-icon size="20" left>mdi-plus</v-icon>
                        Upload videos
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-title>

                <LiveQueueStrip v-if="featureFlags.analysisQueue" class="mb-3" />

                <GroupTabs
                  v-if="groupsEnabled"
                  v-model="activeGroupId"
                  :groups="groups"
                  :total-count="allVideosCount"
                  :loading="loadingGroups"
                  class="mb-3"
                  @input="onGroupTabChange"
                  @create-group="openCreateGroupDialog"
                  @add-videos="openAddToGroupForGroup"
                  @rerun-group="openRunGroupDialog"
                  @open-group="openGroup"
                />

                <v-alert
                  v-if="messageUpload"
                  outlined
                  :color="successfullUpload ? 'success' : 'error'"
                  :icon="successfullUpload ? 'mdi-check-circle' : 'mdi-alert'"
                  dismissible
                >
                  {{ messageUpload }}
                </v-alert>

                <v-alert
                  v-if="messageDelete"
                  outlined
                  :color="successfullDelete ? 'success' : 'error'"
                  :icon="successfullDelete ? 'mdi-check-circle' : 'mdi-alert'"
                  dismissible
                >
                  {{ messageDelete }}
                </v-alert>

                <!-- Single toolbar: filters left, all actions right (icon-only with tooltips) -->
                <v-row class="mx-0 mb-3 align-center manual-toolbar-row">
                  <v-col cols="12" sm="auto" class="pa-0 pr-sm-2 d-flex align-center">
                    <v-checkbox
                      :input-value="allInCurrentFolderSelected"
                      :indeterminate="someInCurrentFolderSelected"
                      :disabled="loadingSelectAllInFolder || pagination.totalItems === 0"
                      dense
                      hide-details
                      class="manual-select-all-checkbox mr-2 mt-0 pt-0"
                      :aria-label="`Select all analyses in ${selectedFolderLabel}`"
                      @change="toggleSelectAllInFolder"
                    />
                    <v-chip
                      v-if="selectedCount > 0"
                      x-small
                      color="primary"
                      outlined
                      close
                      @click:close="clearSelectedAnalyses"
                    >
                      {{ selectedCount }} selected
                    </v-chip>
                    <span v-else class="caption grey--text text--darken-1">
                      {{ pagination.totalItems }} videos
                    </span>
                  </v-col>
                  <v-col cols="12" sm="4" md="3" lg="3" class="pa-0 pr-sm-2">
                    <v-select
                      v-model="selectedFolderFilter"
                      :items="folderFilterItems"
                      item-text="text"
                      item-value="value"
                      label="Folder"
                      dense
                      hide-details
                      outlined
                      @change="onFolderFilterChange"
                    />
                  </v-col>
                  <v-col cols="12" sm="4" md="3" lg="3" class="pa-0 pr-sm-2">
                    <v-text-field
                      v-model="searchQuery"
                      label="Search by video name"
                      dense
                      hide-details
                      outlined
                      clearable
                      prepend-inner-icon="mdi-magnify"
                      @input="onSearchQueryInput"
                    />
                  </v-col>
                  <v-spacer />
                  <v-col cols="12" sm="auto" class="pa-0 d-flex align-center manual-toolbar-actions">
                    <!-- Bulk actions (only when there is a selection) -->
                    <template v-if="selectedCount > 0">
                      <v-tooltip top>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            @click="openMoveSelectedDialog()"
                          >
                            <v-icon small>mdi-folder-move</v-icon>
                          </v-btn>
                        </template>
                        <span>Move selected to folder</span>
                      </v-tooltip>
                      <v-tooltip top>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            color="primary"
                            class="manual-analyze-selected"
                            v-bind="attrs"
                            v-on="on"
                            @click="openAnalyzeSelectedDialog"
                          >
                            <v-icon small>mdi-play-circle-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Analyze selected</span>
                      </v-tooltip>
                      <v-tooltip v-if="groupsEnabled" top>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            color="primary"
                            class="manual-add-to-group"
                            v-bind="attrs"
                            v-on="on"
                            @click="openAddToGroupForSelection"
                          >
                            <v-icon small>mdi-flask-plus-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Add selected to group</span>
                      </v-tooltip>
                      <v-tooltip top>
                        <template #activator="{ on, attrs }">
                          <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="openDeleteSelected">
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete selected</span>
                      </v-tooltip>
                      <v-divider vertical class="mx-2" />
                    </template>

                    <ListViewToggle v-model="viewMode" class="mr-2" @input="onViewModeChange" />

                    <!-- Folder management -->
                    <v-tooltip top>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="openCreateFolderDialog">
                          <v-icon small>mdi-folder-plus</v-icon>
                        </v-btn>
                      </template>
                      <span>New folder</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          <v-btn
                            icon
                            small
                            color="error"
                            :disabled="!canDeleteSelectedFolder"
                            @click="openDeleteFolderDialog"
                          >
                            <v-icon small>mdi-folder-remove</v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ canDeleteSelectedFolder ? 'Delete current folder' : 'Select a folder to delete' }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>

                <div>
                  <VideoRunsTable
                    v-if="viewMode === 'table'"
                    :items="analyses"
                    :loading="loading"
                    :selected="selectedAnalyses"
                    :groups="groups"
                    :folders="folders"
                    :refresh-token="tableRefreshToken"
                    @update:selected="selectedAnalyses = $event"
                    @open="viewAnalysis"
                    @launch="openLaunchForItem"
                    @add-to-group="openAddToGroupForItems([$event])"
                    @typology="openTypologyDialog"
                    @move="openMoveSelectedDialog([$event])"
                    @delete="openDeleteForItems([$event])"
                    @select-group="selectGroupTab"
                    @notify="notify"
                    @membership-changed="onMembershipChanged"
                  />
                  <VideoCardsGrid
                    v-else
                    :items="analyses"
                    :selected-ids="selectedAnalysisIds"
                    @open="viewAnalysis"
                    @toggle-select="toggleAnalysisSelection($event.item, $event.checked)"
                    @typology="openTypologyDialog"
                  />

                  <v-row class="mt-3" justify="center">
                    <v-pagination
                      v-model="pagination.page"
                      class="manual-pagination"
                      :length="pagination.totalPages || 1"
                      total-visible="7"
                      @input="handlePageChange"
                    />
                  </v-row>
                </div>
              </v-col>
            </v-row>

            <v-alert
              v-if="messageAnalyses"
              class="mt-4"
              outlined
              :color="successfullAnalyses ? 'success' : 'error'"
              :icon="successfullAnalyses ? 'mdi-check-circle' : 'mdi-alert'"
              dismissible
            >
              {{ messageAnalyses }}
            </v-alert>
          </v-col>
        </v-row>
      </v-flex>
    </v-layout>

    <UploadVideosDialog
      v-model="uploadVideoDialog"
      :analysis-configs="analysisConfigs"
      :loading-analysis-configs="loadingAnalysisConfigs"
      :folders="folders"
      :default-folder-id="uploadDefaultFolderId"
      :uploading="uploading"
      :upload-progress="uploadProgress"
      :upload-status="uploadStatus"
      :error="uploadError"
      @upload="uploadVideos"
      @cancel="uploadError = ''"
    />

    <!-- Delete Analysis Modal -->
    <v-dialog v-model="deleteAnalysisDialog" max-width="500">
      <v-card>
        <v-card-title class="px-6">
          <h5 class="primary-color">Delete Analysis</h5>
        </v-card-title>
        <v-card-text class="mt-4 pb-0 px-6">
          <p class="mb-2 font-weight-medium">
            You are about to delete {{ deleteSelectionCount }} analysis(es) from
            <strong>{{ selectedFolderLabel }}</strong>.
          </p>
          <p class="mb-0 error--text text--darken-2">
            This action is irreversible. Deleted analyses cannot be recovered.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text class="cancel-modal" @click="closeDeleteDialog"> Cancel </v-btn>
          <template v-if="!deleting">
            <v-btn class="ok-modal" @click="deleteAnalysis"> Confirm </v-btn>
          </template>
          <v-btn v-else class="ok-modal" disabled @click="deleteAnalysis">
            <v-progress-circular indeterminate color="primary" />
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createFolderDialog" max-width="440">
      <v-card>
        <v-card-title class="px-6">
          <h5 class="primary-color">Create folder</h5>
        </v-card-title>
        <v-card-text class="mt-4 pb-0 px-6">
          <v-text-field
            v-model="newFolderName"
            label="Folder name"
            dense
            maxlength="120"
            counter
            autofocus
            @keydown.enter.prevent="createFolder"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text class="cancel-modal" @click="closeCreateFolderDialog">Cancel</v-btn>
          <v-btn class="ok-modal" :disabled="!newFolderName.trim()" @click="createFolder">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteFolderDialog" max-width="500">
      <v-card>
        <v-card-title class="px-6">
          <h5 class="primary-color">Delete Folder</h5>
        </v-card-title>
        <v-card-text class="mt-4 pb-0 px-6">
          <p class="mb-2 font-weight-medium">
            You are about to delete folder <strong>{{ selectedFolderLabel }}</strong>.
          </p>
          <p class="mb-0 error--text text--darken-2">
            All videos in this folder will become unassigned. This action cannot be undone.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text class="cancel-modal" @click="closeDeleteFolderDialog">Cancel</v-btn>
          <v-btn class="ok-modal" :disabled="deletingFolder" @click="confirmDeleteCurrentFolder">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="moveSelectedDialog" max-width="480">
      <v-card>
        <v-card-title class="px-6">
          <h5 class="primary-color">Move {{ moveTargetIds.length }} video(s)</h5>
        </v-card-title>
        <v-card-text class="mt-4 pb-0 px-6">
          <v-select
            v-model="bulkFolderId"
            :items="bulkFolderItems"
            item-text="text"
            item-value="value"
            label="Move to"
            dense
            outlined
            hide-details
            class="mb-3"
            attach
          />
          <p class="mb-0 caption grey--text text--darken-1">
            All selected videos will be moved to <strong>{{ moveTargetFolderLabel }}</strong>.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text class="cancel-modal" @click="closeMoveSelectedDialog">Cancel</v-btn>
          <v-btn
            class="ok-modal"
            :disabled="movingSelected"
            :loading="movingSelected"
            @click="confirmMoveSelectedToFolder"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <GestureTypologyDialog
      v-model="typologyDialogOpen"
      source-type="manual_analysis"
      :source-id="typologyTarget ? typologyTarget.id : null"
      :analysis-name="typologyTargetName"
    />

    <LaunchAnalysisDialog
      v-model="launchAnalysisDialog"
      source-type="manual_analysis"
      :targets="launchAnalysisTargets"
      :analysis-types="analysisConfigs"
      :loading-analysis-types="loadingReanalyzeAnalysisTypes"
      :socket-id="$socket && $socket.id ? $socket.id : ''"
      :mode="groupsEnabled ? 'groups' : 'default'"
      :groups="groupsEnabled ? groups : null"
      :initial-group-ids="launchInitialGroupIds"
      @launched="onAnalysisLaunched"
    />

    <template v-if="groupsEnabled">
      <AddToGroupDialog
        v-model="addToGroupDialog"
        :items="addToGroupItems"
        :groups="groups"
        :locked-group-id="addToGroupLockedId"
        allow-folder
        @added="onAddedToGroup"
        @create-group="openCreateGroupDialog"
      />
      <RunGroupDialog v-model="runGroupDialog" :group="runGroupTarget" @launched="onGroupRunLaunched" />
      <AnalysisGroupDialog
        v-model="groupDialog"
        :analysis-types="analysisConfigs"
        :loading-analysis-types="loadingAnalysisConfigs"
        source-type="manual_analysis"
        @saved="onGroupSaved"
      />
    </template>

    <v-snackbar v-model="snackbar.open" :color="snackbar.color" :timeout="4000" bottom>
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import GestureTypologyDialog from '@/components/analysis/GestureTypologyDialog.vue';
import LaunchAnalysisDialog from '@/components/analysis/runs/LaunchAnalysisDialog.vue';
import GroupTabs from '@/components/manualAnalysis/GroupTabs.vue';
import ListViewToggle from '@/components/manualAnalysis/ListViewToggle.vue';
import UploadVideosDialog from '@/components/manualAnalysis/UploadVideosDialog.vue';
import VideoCardsGrid from '@/components/manualAnalysis/VideoCardsGrid.vue';
import VideoRunsTable from '@/components/manualAnalysis/VideoRunsTable.vue';
import LiveQueueStrip from '@/components/queue/LiveQueueStrip.vue';
import featureFlags from '@/config/featureFlags';
import InterviewsDataService from '@/services/InterviewsDataService';
import ManualAnalysisService from '@/services/ManualAnalysisService';
import SocketioService from '@/services/socketioService';
import { buildRunOptions } from '@/utils/engineRules';
import { readListViewMode } from '@/utils/listViewMode';

const TABLE_ITEMS_PER_PAGE = 20;
const SOCKET_EVENT = 'analysisJobsChanged';
const SOCKET_RELOAD_DEBOUNCE_MS = 1500;
const TERMINAL_STATUSES = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

/**
 * Manual video list (NEXT Semantica). M4 F13 split it into components: UploadVideosDialog
 * (form + lie labelling), VideoCardsGrid (cards mode), VideoRunsTable (table mode with runs by
 * group), GroupTabs (one tab per analysis group, `?group_id=`) and ListViewToggle (persisted in
 * localStorage). The group dialogs (AddToGroupDialog, RunGroupDialog, AnalysisGroupDialog) are
 * loaded lazily so the flag-off page never pulls the groups client.
 */
export default {
  name: 'ManualVideoAnalysisList',
  components: {
    GestureTypologyDialog,
    GroupTabs,
    LaunchAnalysisDialog,
    ListViewToggle,
    LiveQueueStrip,
    UploadVideosDialog,
    VideoCardsGrid,
    VideoRunsTable,
    AddToGroupDialog: () => import('@/components/groups/AddToGroupDialog.vue'),
    RunGroupDialog: () => import('@/components/groups/RunGroupDialog.vue'),
    AnalysisGroupDialog: () => import('@/components/groups/AnalysisGroupDialog.vue'),
  },
  data() {
    return {
      analyses: [],
      loading: false,
      successfullAnalyses: false,
      messageAnalyses: '',
      successfullUpload: false,
      messageUpload: '',
      successfullDelete: false,
      messageDelete: '',
      selectedAnalyses: [],
      loadingSelectAllInFolder: false,
      uiPreferencesHydrated: false,
      uiPreferencesSaveTimer: null,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        totalPages: 1,
        totalItems: 0,
      },
      uploadVideoDialog: false,
      uploading: false,
      uploadProgress: 0,
      uploadStatus: '',
      uploadError: '',
      analysisConfigs: [],
      loadingAnalysisConfigs: false,
      folders: [],
      selectedFolderFilter: 'all',
      searchQuery: '',
      searchDebounceTimer: null,
      bulkFolderId: null,
      createFolderDialog: false,
      newFolderName: '',
      deleteAnalysisDialog: false,
      deleting: false,
      deleteTargetIds: [],
      deleteFolderDialog: false,
      deletingFolder: false,
      moveSelectedDialog: false,
      movingSelected: false,
      moveTargetIds: [],
      launchAnalysisDialog: false,
      launchAnalysisTargets: [], // [{ id, name, analysisTypeId }]
      launchInitialGroupIds: [],
      loadingReanalyzeAnalysisTypes: false,
      typologyDialogOpen: false,
      typologyTarget: null,
      // M4 F13: cards / table and analysis groups.
      viewMode: readListViewMode(),
      groups: [],
      loadingGroups: false,
      activeGroupId: null,
      allVideosCount: 0,
      addToGroupDialog: false,
      addToGroupItems: [],
      addToGroupLockedId: null,
      runGroupDialog: false,
      runGroupTarget: null,
      groupDialog: false,
      tableRefreshToken: 0,
      socketReloadTimer: null,
      snackbar: { open: false, message: '', color: 'success' },
    };
  },
  computed: {
    featureFlags() {
      return featureFlags;
    },
    groupsEnabled() {
      return Boolean(featureFlags.analysisGroups);
    },
    deleteSelectionCount() {
      return this.deleteTargetIds.length;
    },
    selectedAnalysisIds() {
      const seen = new Set();
      const out = [];
      this.selectedAnalyses.forEach((item) => {
        const id = Number(item?.id);
        if (!Number.isInteger(id) || id <= 0 || seen.has(id)) return;
        seen.add(id);
        out.push(id);
      });
      return out;
    },
    selectedCount() {
      return this.selectedAnalysisIds.length;
    },
    allInCurrentFolderSelected() {
      const total = Number(this.pagination.totalItems || 0);
      return total > 0 && this.selectedCount === total;
    },
    someInCurrentFolderSelected() {
      const total = Number(this.pagination.totalItems || 0);
      return this.selectedCount > 0 && this.selectedCount < total;
    },
    selectedFolderLabel() {
      if (this.selectedFolderFilter === 'all') return 'all folders';
      if (this.selectedFolderFilter === 'unassigned') return 'Unassigned';
      const selectedId = Number(this.selectedFolderFilter);
      const folder = this.folders.find((item) => Number(item?.id) === selectedId);
      return folder?.name || 'selected folder';
    },
    moveTargetFolderLabel() {
      if (this.bulkFolderId == null) return 'Unassigned';
      const targetId = Number(this.bulkFolderId);
      const folder = this.folders.find((item) => Number(item?.id) === targetId);
      return folder?.name || 'selected folder';
    },
    folderFilterItems() {
      const dynamicFolders = this.folders.map((folder) => ({ text: folder.name, value: folder.id }));
      return [
        { text: 'All videos', value: 'all' },
        { text: 'Unassigned', value: 'unassigned' },
        ...dynamicFolders,
      ];
    },
    bulkFolderItems() {
      const dynamicFolders = this.folders.map((folder) => ({ text: folder.name, value: folder.id }));
      return [{ text: 'Unassigned', value: null }, ...dynamicFolders];
    },
    canDeleteSelectedFolder() {
      const folderId = Number(this.selectedFolderFilter);
      return Number.isInteger(folderId) && folderId > 0;
    },
    /** Folder preselected in the upload dialog: the folder being filtered, if any. */
    uploadDefaultFolderId() {
      const selectedFolderAsNumber = Number(this.selectedFolderFilter);
      return Number.isInteger(selectedFolderAsNumber) && selectedFolderAsNumber > 0
        ? selectedFolderAsNumber
        : null;
    },
    typologyTargetName() {
      const target = this.typologyTarget;
      if (!target) return '';
      return target.name || target.original_filename || `Analysis ${target.id}`;
    },
    activeGroup() {
      if (this.activeGroupId == null) return null;
      return this.groups.find((group) => String(group.id) === String(this.activeGroupId)) || null;
    },
  },
  async mounted() {
    await this.ensureUiPreferencesLoaded();
    window.addEventListener('resize', this.onViewportResize, { passive: true });
    SocketioService.emisor.$on(SOCKET_EVENT, this.onJobsChanged);
    if (this.groupsEnabled) {
      this.activeGroupId = this.normalizeGroupId(this.$route?.query?.group_id);
      this.loadGroups();
    }
    await this.loadInterviewAnalysisConfigs();
    await this.loadFolders();
    this.updateItemsPerPage(true);
  },
  beforeDestroy() {
    if (this.uiPreferencesSaveTimer) {
      clearTimeout(this.uiPreferencesSaveTimer);
      this.uiPreferencesSaveTimer = null;
    }
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    if (this.socketReloadTimer) {
      clearTimeout(this.socketReloadTimer);
      this.socketReloadTimer = null;
    }
    SocketioService.emisor.$off(SOCKET_EVENT, this.onJobsChanged);
    window.removeEventListener('resize', this.onViewportResize);
  },
  watch: {
    selectedFolderFilter() {
      this.scheduleSaveUiPreferences();
    },
  },
  methods: {
    // ----- notifications -------------------------------------------------------------------
    notify({ message, type = 'success' } = {}) {
      if (!message) return;
      this.snackbar = { open: true, message, color: type === 'error' ? 'error' : type };
    },
    /** Legacy page alert (kept for the messages the old view rendered under the toolbar). */
    showDeleteMessage(message, ok) {
      this.successfullDelete = Boolean(ok);
      this.messageDelete = '';
      setTimeout(() => {
        this.messageDelete = message;
      }, 10);
    },

    // ----- preferences ---------------------------------------------------------------------
    normalizeSelectedFolderFilter(value) {
      if (value === 'all' || value === 'unassigned') return value;
      const numericValue = Number(value);
      if (Number.isInteger(numericValue) && numericValue > 0) return numericValue;
      return 'all';
    },
    async ensureUiPreferencesLoaded() {
      this.uiPreferencesHydrated = false;
      try {
        const response = await ManualAnalysisService.getUiPreferences();
        const selectedFolderFilter = response?.data?.data?.selectedFolderFilter;
        this.selectedFolderFilter = this.normalizeSelectedFolderFilter(selectedFolderFilter);
      } catch (error) {
        this.selectedFolderFilter = 'all';
      } finally {
        this.uiPreferencesHydrated = true;
      }
    },
    scheduleSaveUiPreferences() {
      if (!this.uiPreferencesHydrated) return;
      if (this.uiPreferencesSaveTimer) clearTimeout(this.uiPreferencesSaveTimer);
      this.uiPreferencesSaveTimer = setTimeout(() => {
        this.saveUiPreferences();
      }, 350);
    },
    async saveUiPreferences() {
      if (!this.uiPreferencesHydrated) return;
      try {
        await ManualAnalysisService.setUiPreferences({
          listViewMode: this.viewMode,
          selectedFolderFilter: String(this.normalizeSelectedFolderFilter(this.selectedFolderFilter)),
        });
      } catch (error) {
        // Do not block UX if saving fails.
      }
    },
    onViewModeChange() {
      this.scheduleSaveUiPreferences();
      this.updateItemsPerPage(true);
    },

    // ----- reference data ------------------------------------------------------------------
    async loadInterviewAnalysisConfigs() {
      this.loadingAnalysisConfigs = true;
      try {
        const response = await InterviewsDataService.findAllInterviewAnalysisTypes(1, 100);
        if (response.status === 200) this.analysisConfigs = response.data.data || [];
      } catch (error) {
        console.error('Error loading interview analysis configs', error);
      } finally {
        this.loadingAnalysisConfigs = false;
      }
    },
    async loadFolders() {
      try {
        const response = await ManualAnalysisService.getFolders();
        this.folders = Array.isArray(response?.data?.data) ? response.data.data : [];
      } catch (error) {
        this.folders = [];
      }
    },
    async loadGroups() {
      if (!this.groupsEnabled) return;
      this.loadingGroups = true;
      try {
        const { default: AnalysisGroupsService } = await import('@/services/AnalysisGroupsService');
        const response = await AnalysisGroupsService.list({ limit: 200 });
        const body = response?.data?.data ?? response?.data ?? {};
        this.groups = Array.isArray(body.items) ? body.items : Array.isArray(body) ? body : [];
      } catch (error) {
        this.groups = [];
      } finally {
        this.loadingGroups = false;
      }
    },

    // ----- listing -------------------------------------------------------------------------
    onViewportResize() {
      this.updateItemsPerPage();
    },
    getCardsItemsPerPage() {
      if (this.$vuetify.breakpoint.smAndDown) return 10;
      if (this.$vuetify.breakpoint.xl) return 16; // 4 cols x 4 rows
      if (this.$vuetify.breakpoint.lg) return 12; // 4 cols x 3 rows
      if (this.$vuetify.breakpoint.md) return 9; // 3 cols x 3 rows
      return 10;
    },
    updateItemsPerPage(forceReload = false) {
      const nextItemsPerPage =
        this.viewMode === 'table' ? TABLE_ITEMS_PER_PAGE : this.getCardsItemsPerPage();
      if (!forceReload && this.pagination.itemsPerPage === nextItemsPerPage) return;
      this.pagination.itemsPerPage = nextItemsPerPage;
      this.pagination.page = 1;
      this.getAllAnalyses(1);
    },
    getFolderQueryParam() {
      if (this.selectedFolderFilter === 'all') return undefined;
      if (this.selectedFolderFilter === 'unassigned') return 'unassigned';
      return Number(this.selectedFolderFilter);
    },
    getSearchQueryParam() {
      const normalizedSearch = String(this.searchQuery || '').trim();
      return normalizedSearch.length > 0 ? normalizedSearch : undefined;
    },
    listParams(page, limit) {
      return {
        page,
        limit,
        folderId: this.getFolderQueryParam(),
        search: this.getSearchQueryParam(),
        groupId: this.groupsEnabled ? this.activeGroupId : null,
      };
    },
    onSearchQueryInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.clearSelectedAnalyses();
        this.getAllAnalyses(1);
      }, 300);
    },
    onFolderFilterChange() {
      this.pagination.page = 1;
      this.clearSelectedAnalyses();
      this.getAllAnalyses(1);
    },
    getAllAnalyses(page) {
      this.loading = true;
      const groupScoped = this.groupsEnabled && this.activeGroupId != null;
      return ManualAnalysisService.list(this.listParams(page, this.pagination.itemsPerPage)).then(
        (response) => {
          if (response.status == 200) {
            this.analyses = response.data.data;
            // Selection is preserved across pagination; only filter/search/folder
            // changes should clear it (handled in those handlers explicitly).
            this.pagination.page = page;
            this.pagination.totalPages = response.data.links.totalPages || 1;
            this.pagination.totalItems =
              response.data.links.totalItems ||
              this.pagination.totalPages * this.pagination.itemsPerPage;
            if (!groupScoped) this.allVideosCount = this.pagination.totalItems;
          } else {
            this.successfullAnalyses = false;
            this.messageAnalyses =
              'An error has occurred retrieving the list of analyses. Please, try again later.';
          }
          this.loading = false;
        },
        (error) => {
          this.successfullAnalyses = false;
          this.messageAnalyses =
            'An error has occurred retrieving the list of analyses. Please, try again later.';
          console.error((error.response && error.response.data) || error.message || error.toString());
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            localStorage.removeItem('user');
            this.$router.push('/');
          }
          this.loading = false;
        }
      );
    },
    handlePageChange(newPage) {
      this.pagination.page = newPage;
      this.getAllAnalyses(newPage);
    },
    scheduleReload() {
      if (this.socketReloadTimer) clearTimeout(this.socketReloadTimer);
      this.socketReloadTimer = setTimeout(() => {
        this.socketReloadTimer = null;
        this.getAllAnalyses(this.pagination.page || 1);
        this.tableRefreshToken += 1;
      }, SOCKET_RELOAD_DEBOUNCE_MS);
    },
    /** Terminal transitions of manual runs refresh the page (SQL status, pointer, group counts). */
    onJobsChanged(event) {
      if (!event || event.source_type !== 'manual_analysis') return;
      if (!TERMINAL_STATUSES.includes(String(event.status || '').toUpperCase())) return;
      this.scheduleReload();
      if (this.groupsEnabled && event.group_id != null) this.loadGroups();
    },
    viewAnalysis(item) {
      this.$router.push(`/coherence-analysis/${item.id}`);
    },
    openTypologyDialog(item) {
      this.typologyTarget = item;
      this.typologyDialogOpen = true;
    },

    // ----- selection -----------------------------------------------------------------------
    setSelectedAnalysisIds(ids) {
      const byId = new Map(this.selectedAnalyses.map((item) => [Number(item?.id), item]));
      const normalized = Array.from(
        new Set((ids || []).map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))
      );
      this.selectedAnalyses = normalized.map((id) => byId.get(id) || { id });
    },
    async getAllAnalysisIdsForCurrentFilter() {
      const perPage = 200;
      const ids = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await ManualAnalysisService.list(this.listParams(page, perPage));
        const items = Array.isArray(response?.data?.data) ? response.data.data : [];
        items.forEach((item) => {
          const id = Number(item?.id);
          if (Number.isInteger(id) && id > 0) ids.push(id);
        });
        totalPages = Number(response?.data?.links?.totalPages || 1);
        page += 1;
      } while (page <= totalPages);
      return Array.from(new Set(ids));
    },
    async toggleSelectAllInFolder(checked) {
      if (!checked) {
        this.clearSelectedAnalyses();
        return;
      }
      this.loadingSelectAllInFolder = true;
      try {
        const allIds = await this.getAllAnalysisIdsForCurrentFilter();
        this.setSelectedAnalysisIds(allIds);
      } catch (error) {
        this.showDeleteMessage('Could not select all analyses for this folder.', false);
      } finally {
        this.loadingSelectAllInFolder = false;
      }
    },
    clearSelectedAnalyses() {
      this.selectedAnalyses = [];
    },
    isAnalysisSelected(item) {
      return this.selectedAnalysisIds.includes(Number(item?.id));
    },
    toggleAnalysisSelection(item, checked) {
      const shouldSelect = Boolean(checked);
      const alreadySelected = this.isAnalysisSelected(item);
      if (shouldSelect && !alreadySelected) {
        this.selectedAnalyses = [...this.selectedAnalyses, item];
      } else if (!shouldSelect && alreadySelected) {
        this.selectedAnalyses = this.selectedAnalyses.filter((selected) => selected.id !== item.id);
      }
    },
    /** Selection as group items: [{ source_type, source_id, name }]. */
    selectionAsGroupItems(items = this.selectedAnalyses) {
      return items.map((item) => ({
        source_type: 'manual_analysis',
        source_id: Number(item.id),
        name: item.name || item.original_filename || `Analysis ${item.id}`,
      }));
    },

    // ----- folders -------------------------------------------------------------------------
    openCreateFolderDialog() {
      this.newFolderName = '';
      this.createFolderDialog = true;
    },
    closeCreateFolderDialog() {
      this.createFolderDialog = false;
      this.newFolderName = '';
    },
    async createFolder() {
      const name = this.newFolderName.trim();
      if (!name) return;
      try {
        await ManualAnalysisService.createFolder(name);
        await this.loadFolders();
        const createdFolder = this.folders.find(
          (folder) => folder.name.toLowerCase() === name.toLowerCase()
        );
        if (createdFolder) {
          this.selectedFolderFilter = createdFolder.id;
          this.bulkFolderId = createdFolder.id;
        }
        this.closeCreateFolderDialog();
        this.onFolderFilterChange();
      } catch (error) {
        this.notify({
          message: error?.response?.data?.short || 'Could not create folder. Please try again.',
          type: 'error',
        });
      }
    },
    openDeleteFolderDialog() {
      if (!this.canDeleteSelectedFolder) return;
      this.deleteFolderDialog = true;
    },
    closeDeleteFolderDialog() {
      this.deleteFolderDialog = false;
    },
    async confirmDeleteCurrentFolder() {
      if (!this.canDeleteSelectedFolder) return;
      const folderId = Number(this.selectedFolderFilter);
      this.deletingFolder = true;
      try {
        await ManualAnalysisService.deleteFolder(folderId);
        this.selectedFolderFilter = 'all';
        this.bulkFolderId = null;
        await this.loadFolders();
        this.getAllAnalyses(1);
        this.closeDeleteFolderDialog();
      } catch (error) {
        this.showDeleteMessage('Could not delete folder. Please try again.', false);
      } finally {
        this.deletingFolder = false;
      }
    },
    /** Move the selection (default) or the given rows to a folder. */
    openMoveSelectedDialog(items = null) {
      this.moveTargetIds = items
        ? items.map((item) => Number(item.id)).filter((id) => Number.isInteger(id) && id > 0)
        : [...this.selectedAnalysisIds];
      if (this.moveTargetIds.length === 0) return;
      if (this.bulkFolderId === undefined) this.bulkFolderId = null;
      this.moveSelectedDialog = true;
    },
    closeMoveSelectedDialog() {
      this.moveSelectedDialog = false;
    },
    async confirmMoveSelectedToFolder() {
      if (this.moveTargetIds.length === 0) return;
      this.movingSelected = true;
      try {
        const response = await ManualAnalysisService.bulkUpdateFolder(this.moveTargetIds, this.bulkFolderId);
        const updatedCount = Number(response?.data?.data?.updatedCount || 0);
        this.showDeleteMessage(`${updatedCount} video(s) moved successfully.`, true);
        this.clearSelectedAnalyses();
        this.getAllAnalyses(this.pagination.page || 1);
        this.closeMoveSelectedDialog();
      } catch (error) {
        this.showDeleteMessage('Could not move selected videos.', false);
      } finally {
        this.movingSelected = false;
      }
    },

    // ----- upload --------------------------------------------------------------------------
    openUploadVideo() {
      this.uploadError = '';
      this.uploading = false;
      this.uploadProgress = 0;
      this.uploadStatus = '';
      this.uploadVideoDialog = true;
    },
    /** Uploads every file of the dialog request (one POST per video, sequential). */
    async uploadVideos(request) {
      const files = Array.isArray(request?.files) ? request.files : [];
      if (files.length === 0 || !request.selectedAnalysisConfigId) return;
      const uploadRunOptions = JSON.stringify(buildRunOptions({ engines: request.engines }));

      this.uploading = true;
      this.uploadError = '';
      this.uploadProgress = 0;
      this.uploadStatus = 'Uploading videos...';

      try {
        const totalFiles = files.length;
        const successfulUploads = [];
        const failedUploads = [];

        for (let index = 0; index < totalFiles; index++) {
          const file = files[index];
          const formData = new FormData();
          formData.append('video', file);
          formData.append('analysisConfigId', String(request.selectedAnalysisConfigId));
          formData.append('runOptions', uploadRunOptions);
          if (request.folderId !== undefined) {
            formData.append('folderId', request.folderId === null ? 'null' : String(request.folderId));
          }
          formData.append('name', totalFiles === 1 ? request.name || file.name : file.name);
          formData.append('description', request.description || '');

          // Attach the label from the pre-upload stepper: either the lie range or
          // the truthful mark (both are persisted as a video_incoherence row).
          const incoherenceLabel = request.incoherenceLabels?.[index];
          if (incoherenceLabel) {
            if (incoherenceLabel.noIncoherence) {
              formData.append('incoherenceTruthful', 'true');
            } else if (incoherenceLabel.endMs > incoherenceLabel.startMs) {
              formData.append('incoherenceStartMs', String(Math.round(incoherenceLabel.startMs)));
              formData.append('incoherenceEndMs', String(Math.round(incoherenceLabel.endMs)));
            }
          }

          // Add socket ID for real-time updates
          const socketId = this.$socket?.id || null;
          if (socketId) formData.append('socketId', socketId);

          this.uploadStatus = `Uploading ${index + 1}/${totalFiles}: ${file.name}`;
          try {
            const response = await ManualAnalysisService.uploadVideo(formData, (progress) => {
              this.uploadProgress = Math.round(((index + progress / 100) / totalFiles) * 100);
            });
            // 201 since uploads only register the video (no analysis launched); 202 from older APIs.
            if (response.status === 201 || response.status === 202) successfulUploads.push(file.name);
            else failedUploads.push(file.name);
          } catch (error) {
            failedUploads.push(file.name);
            console.error('Upload error:', error);
          }
        }

        this.messageUpload = '';
        this.successfullUpload = failedUploads.length === 0 && successfulUploads.length > 0;
        let message;
        if (failedUploads.length === 0) {
          message = `${successfulUploads.length} video(s) uploaded successfully. Analyses started.`;
        } else if (successfulUploads.length > 0) {
          this.successfullUpload = false;
          message = `Uploaded ${successfulUploads.length}/${totalFiles} video(s). Failed: ${failedUploads.join(', ')}`;
        } else {
          message = 'An error has occurred uploading videos. Please, try again later.';
        }
        setTimeout(() => {
          this.messageUpload = message;
        }, 10);

        this.getAllAnalyses(1);
        this.uploading = false;
        this.uploadVideoDialog = false;
      } catch (error) {
        console.error('Upload error:', error);
        this.uploading = false;
        this.uploadError =
          error.response?.data?.message ||
          'An error has occurred uploading the video. Please, try again later.';
      }
    },

    // ----- launch analysis -----------------------------------------------------------------
    async ensureAnalysisConfigs() {
      if (Array.isArray(this.analysisConfigs) && this.analysisConfigs.length > 0) return;
      this.loadingReanalyzeAnalysisTypes = true;
      try {
        await this.loadInterviewAnalysisConfigs();
      } finally {
        this.loadingReanalyzeAnalysisTypes = false;
      }
    },
    launchTargetOf(item) {
      return {
        id: Number(item.id),
        name: item.name || item.original_filename || `Analysis ${item.id}`,
        analysisTypeId: item.id_interview_analysis_config || null,
      };
    },
    /** 'Analyze selected': every selected video (also those picked on other pages). */
    async openAnalyzeSelectedDialog() {
      if (this.selectedCount === 0) return;
      await this.ensureAnalysisConfigs();
      this.launchAnalysisTargets = this.selectedAnalyses.map(this.launchTargetOf);
      this.launchInitialGroupIds = this.activeGroupId != null ? [this.activeGroupId] : [];
      this.launchAnalysisDialog = true;
    },
    async openLaunchForItem(item) {
      await this.ensureAnalysisConfigs();
      this.launchAnalysisTargets = [this.launchTargetOf(item)];
      this.launchInitialGroupIds = this.activeGroupId != null ? [this.activeGroupId] : [];
      this.launchAnalysisDialog = true;
    },
    watchLaunchedRuns(accepted) {
      const runIds = (accepted || []).map((entry) => entry.run_id).filter(Boolean);
      if (runIds.length && this.$store?._actions?.['analysisQueue/watchRuns']) {
        this.$store.dispatch('analysisQueue/watchRuns', runIds);
      }
    },
    onAnalysisLaunched(result) {
      this.watchLaunchedRuns(result?.accepted);
      this.launchAnalysisTargets = [];
      this.messageDelete = '';
      if (Array.isArray(result?.rejected) && result.rejected.length) {
        const reasons = result.rejected
          .map((entry) => `#${entry.id}${entry.reason ? ` (${entry.reason})` : ''}`)
          .join(', ');
        this.showDeleteMessage(`Not queued: ${reasons}.`, false);
      }
      if (Array.isArray(result?.groupIds) && result.groupIds.length) this.loadGroups();
      setTimeout(() => {
        this.getAllAnalyses(this.pagination.page || 1);
        this.tableRefreshToken += 1;
      }, 800);
    },

    // ----- analysis groups (M4) ------------------------------------------------------------
    normalizeGroupId(value) {
      if (value === undefined || value === null || value === '') return null;
      const n = Number(value);
      return Number.isInteger(n) && n > 0 ? n : null;
    },
    /** Group tab selection: `?group_id=` on the route and on the list call, selection cleared. */
    onGroupTabChange(groupId) {
      this.activeGroupId = this.normalizeGroupId(groupId);
      const query = { ...(this.$route?.query || {}) };
      if (this.activeGroupId == null) delete query.group_id;
      else query.group_id = String(this.activeGroupId);
      if (this.$router && String(query.group_id ?? '') !== String(this.$route?.query?.group_id ?? '')) {
        this.$router.replace({ query }).catch(() => {});
      }
      this.pagination.page = 1;
      this.clearSelectedAnalyses();
      this.getAllAnalyses(1);
    },
    selectGroupTab(groupId) {
      if (!this.groupsEnabled) return;
      this.onGroupTabChange(groupId);
    },
    openGroup(group) {
      if (group?.id != null) this.$router.push(`/analysis-groups/${group.id}`);
    },
    openCreateGroupDialog() {
      this.addToGroupDialog = false;
      this.groupDialog = true;
    },
    async onGroupSaved(group) {
      await this.loadGroups();
      this.notify({ message: `Group ${group?.name || ''} created.`.replace('  ', ' ') });
      if (group?.id != null) {
        this.addToGroupLockedId = group.id;
        if (this.selectedCount > 0) {
          this.addToGroupItems = this.selectionAsGroupItems();
          this.addToGroupDialog = true;
        }
      }
    },
    openAddToGroupForItems(items) {
      this.addToGroupItems = this.selectionAsGroupItems(items);
      this.addToGroupLockedId = null;
      this.addToGroupDialog = true;
    },
    openAddToGroupForSelection() {
      if (this.selectedCount === 0) return;
      this.openAddToGroupForItems(this.selectedAnalyses);
    },
    /** 'Add videos' from a group tab: current selection (or the folder tab) into that group. */
    openAddToGroupForGroup(group) {
      this.addToGroupItems = this.selectionAsGroupItems();
      this.addToGroupLockedId = group?.id ?? null;
      this.addToGroupDialog = true;
    },
    onAddedToGroup(result) {
      const added = Number(result?.added ?? 0);
      const skipped = Number(result?.skipped ?? 0);
      const parts = [`${added} video(s) added to the group`];
      if (skipped > 0) parts.push(`${skipped} already members`);
      this.notify({ message: `${parts.join(', ')}.` });
      this.addToGroupLockedId = null;
      this.loadGroups();
      this.tableRefreshToken += 1;
      if (this.activeGroupId != null) this.getAllAnalyses(this.pagination.page || 1);
    },
    onMembershipChanged() {
      this.loadGroups();
      if (this.activeGroupId != null) this.getAllAnalyses(this.pagination.page || 1);
    },
    openRunGroupDialog(group) {
      this.runGroupTarget = group || this.activeGroup;
      if (!this.runGroupTarget) return;
      this.runGroupDialog = true;
    },
    onGroupRunLaunched(result) {
      this.watchLaunchedRuns(result?.accepted);
      const accepted = Array.isArray(result?.accepted) ? result.accepted.length : 0;
      const skipped = Array.isArray(result?.skipped) ? result.skipped.length : 0;
      const parts = [`Queued ${accepted} ${accepted === 1 ? 'run' : 'runs'}`];
      if (skipped > 0) parts.push(`${skipped} skipped`);
      if (result?.batch) parts.push(`batch ${result.batch}`);
      this.notify({ message: `${parts.join(' · ')}.` });
      this.loadGroups();
      setTimeout(() => {
        this.tableRefreshToken += 1;
      }, 800);
    },

    // ----- delete --------------------------------------------------------------------------
    openDeleteSelected() {
      this.deleteTargetIds = [...this.selectedAnalysisIds];
      this.deleteAnalysisDialog = true;
    },
    openDeleteForItems(items) {
      this.deleteTargetIds = items
        .map((item) => Number(item.id))
        .filter((id) => Number.isInteger(id) && id > 0);
      if (this.deleteTargetIds.length) this.deleteAnalysisDialog = true;
    },
    closeDeleteDialog() {
      this.deleteAnalysisDialog = false;
      this.deleteTargetIds = [];
    },
    async deleteAnalysis() {
      if (this.deleteTargetIds.length === 0) {
        this.showDeleteMessage(
          'An error has occurred deleting the analysis. Please, try again later.',
          false
        );
        this.closeDeleteDialog();
        return;
      }

      this.deleting = true;
      this.messageDelete = '';
      this.successfullDelete = false;

      try {
        if (this.deleteTargetIds.length === 1) {
          const response = await ManualAnalysisService.delete(this.deleteTargetIds[0]);
          if (response.status === 204) {
            this.showDeleteMessage('Analysis successfully deleted.', true);
          } else {
            this.showDeleteMessage(
              'An error has occurred deleting the analysis. Please, try again later.',
              false
            );
          }
        } else {
          const response = await ManualAnalysisService.bulkDelete(this.deleteTargetIds);
          const result = response?.data?.data || {};
          const deletedCount = Number(result.deletedCount || 0);
          const failedCount = Number(result.failedCount || 0);
          if (deletedCount > 0 && failedCount === 0) {
            this.showDeleteMessage(`${deletedCount} analyses successfully deleted.`, true);
          } else if (deletedCount > 0 && failedCount > 0) {
            this.showDeleteMessage(
              `Deleted ${deletedCount} analyses. ${failedCount} could not be deleted.`,
              false
            );
          } else {
            this.showDeleteMessage(
              'An error has occurred deleting analyses. Please, try again later.',
              false
            );
          }
        }

        this.closeDeleteDialog();
        this.selectedAnalyses = [];
        this.getAllAnalyses(1);
        if (this.groupsEnabled) this.loadGroups();
      } catch (error) {
        this.showDeleteMessage(
          'An error has occurred deleting analyses. Please, try again later.',
          false
        );
        this.closeDeleteDialog();
        console.error((error.response && error.response.data) || error.message || error.toString());
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          localStorage.removeItem('user');
          this.$router.push('/');
        }
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.manual-header-title {
  margin: 0;
}

.manual-upload-col {
  display: flex;
  align-items: center;
}

.manual-toolbar-row {
  flex-wrap: wrap;
  row-gap: 8px;
  column-gap: 0;
}

.manual-toolbar-actions {
  gap: 2px;
  justify-content: flex-end;
  flex-wrap: nowrap;
}

.manual-toolbar-actions .v-divider--vertical {
  height: 24px;
  min-height: 0;
}

.manual-select-all-checkbox {
  margin-top: 0 !important;
}

.manual-pagination {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 28px;
}

@media (max-width: 959px) {
  .manual-header-row {
    align-items: center;
  }

  .manual-header-title {
    font-size: 1.3rem;
    line-height: 1.25;
  }

  .manual-upload-col {
    margin-top: 8px;
    margin-bottom: 10px;
  }

  .manual-toolbar-row {
    gap: 8px;
  }
}

@media (max-width: 600px) {
  .manual-header-title {
    font-size: 1.1rem;
  }

  .manual-toolbar-actions {
    justify-content: flex-start;
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
